import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';

/**
 * Add / edit a customer's saved card — metadata only (BE-8). Shared by the client portal (PortalCards) and the
 * TC's client page (ClientCardsTab). Never asks for the full card number or the CVV. Editing changes only the
 * nickname and expiry: a different card is a new card.
 */

export const CARD_TYPES = ['VISA', 'MASTERCARD', 'AMEX'];
export const CARD_TYPE_LABELS = { VISA: 'Visa', MASTERCARD: 'MC', AMEX: 'Amex', DINERS: 'Diners' };

const FS = '0.8125rem';
const FIELD_SX = { '& .MuiInputBase-root': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS } };

const emptyForm = {
  nickname: '', cardType: 'VISA', lastFour: '', bankIssuer: '', cardholderName: '', expiryMonth: '', expiryYear: '',
};

const formFor = (card) => {
  if (!card) return emptyForm;
  const [mm, yy] = (card.expiry || '').split('/');
  return {
    nickname: card.nickname || '',
    cardType: card.cardType,
    lastFour: card.lastFour,
    bankIssuer: card.bankIssuer || '',
    cardholderName: card.cardholderName || '',
    expiryMonth: mm || '',
    expiryYear: yy || '',
  };
};

const CustomerCardDialog = ({ open, card, saving, onClose, onSave, note, titleAddon }) => {
  const editing = !!card;
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { if (open) setForm(formFor(card)); }, [open, card]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const digits = (key, max) => (e) => setForm((f) => ({ ...f, [key]: e.target.value.replace(/\D/g, '').slice(0, max) }));

  const hasExpiry = form.expiryMonth || form.expiryYear;
  const expiryValid = !hasExpiry || (/^(0?[1-9]|1[0-2])$/.test(form.expiryMonth) && /^\d{2}$/.test(form.expiryYear));
  const valid = expiryValid && form.nickname.trim() && (editing
    || (form.lastFour.length === 4 && form.bankIssuer.trim() && form.cardholderName.trim()));

  const save = () => {
    const expiry = hasExpiry ? `${form.expiryMonth.padStart(2, '0')}/${form.expiryYear}` : null;
    if (editing) return onSave({ nickname: form.nickname.trim(), expiry });
    const { expiryMonth, expiryYear, ...rest } = form;
    return onSave({
      ...rest,
      nickname: form.nickname.trim(),
      bankIssuer: form.bankIssuer.trim(),
      cardholderName: form.cardholderName.trim(),
      expiry,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {editing ? 'Edit Card' : 'Add Card'}
        {titleAddon}
      </DialogTitle>
      <DialogContent sx={{ pt: 1, ...FIELD_SX }}>
        <TextField
          label="Card Nickname" fullWidth size="small" value={form.nickname} onChange={set('nickname')}
          placeholder="e.g. Rashid's ENBD Visa" sx={{ mb: 1.5, mt: 1 }}
        />

        {!editing && (
          <>
            <TextField label="Card Type" select fullWidth size="small" value={form.cardType} onChange={set('cardType')} sx={{ mb: 1.5 }}>
              {CARD_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: FS }}>{t}</MenuItem>)}
            </TextField>
            <TextField
              label="Last 4 Digits" fullWidth size="small" value={form.lastFour} onChange={digits('lastFour', 4)}
              inputProps={{ maxLength: 4, inputMode: 'numeric' }} helperText="Only the last 4 — never the full number"
              sx={{ mb: 1.5 }}
            />
            <TextField label="Bank Issuer" fullWidth size="small" value={form.bankIssuer} onChange={set('bankIssuer')} sx={{ mb: 1.5 }} />
            <TextField label="Cardholder Name" fullWidth size="small" value={form.cardholderName} onChange={set('cardholderName')} sx={{ mb: 1.5 }} />
          </>
        )}

        <Typography variant="caption" color="text.secondary">Expiry (optional)</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            label="Month" size="small" value={form.expiryMonth} onChange={digits('expiryMonth', 2)}
            inputProps={{ maxLength: 2, placeholder: 'MM', inputMode: 'numeric' }} sx={{ width: 80 }}
          />
          <Typography sx={{ alignSelf: 'center', color: 'text.secondary' }}>/</Typography>
          <TextField
            label="Year" size="small" value={form.expiryYear} onChange={digits('expiryYear', 2)}
            inputProps={{ maxLength: 2, placeholder: 'YY', inputMode: 'numeric' }} sx={{ width: 80 }}
          />
        </Box>
        {!expiryValid && (
          <Typography sx={{ fontSize: '0.75rem', color: 'error.main', mt: -1, mb: 1 }}>Expiry must be MM / YY.</Typography>
        )}

        <Alert severity="info" sx={{ fontSize: '0.75rem', py: 0.5 }}>
          {note || 'This is for tracking only. We do not store your full card number or CVV.'}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid || saving} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : editing ? 'Update' : 'Save Card'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerCardDialog;
